import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../app/store';
import {
  CubeMeasureEnum,
  FeatureEnum,
} from '../../services/data/impl/cube/cubeSlice';
import { CropType } from '../../services/entities/impl/blfApi/schemas/variety';
import { isFeatureEnum } from '../commonUtils';

// use id and name fields to match customer, ranch, and block
export interface Year {
  id: string;
  name: string;
}

export enum ResetItemsEnum {
  Customer = 'Customer',
  Ranch = 'Ranch',
  Block = 'Block',
  Scan = 'Scan',
  Feature = 'Feature',
  AIServiceVersionOverride = 'AIServiceVersionOverride',
  Measure = 'Measure',
  Year = 'Year',
  AllCubeRelatedFields = 'AllCubeRelatedFields',
  RowNum = 'RowNum',
  ViewDirection = 'ViewDirection',
  RowsideId = 'RowsideId',
  PointId = 'PointId',
  HoverPointId = 'HoverPointId',
  HoverRowNum = 'HoverRowNum',
  HoverRowsideId = 'HoverRowsideId',
  CurrRanchViewFeature = 'CurrRanchViewFeature',
  CropTypesOfFilteredBlocks = 'CropTypesOfFilteredBlocks',
}

export enum RanchViewFeaturesEnum {
  RanchMap = 'RanchMap',
  Charts = 'Charts',
  Tables = 'Tables',
}

export enum ScanDataStatusEnum {
  idle = 'idle',
  loading = 'loading',
  success = 'success',
  error = 'error',
}

export interface SelectState {
  currCustomerID: string;
  currRanchID: string;
  currBlockID: string;
  currScanID: string;
  currFeature: FeatureEnum;
  currAIServiceVersionOverride: string;
  currMeasure: CubeMeasureEnum;
  currYear: string;
  currRowNum: string;
  currViewDirection: string;
  currRowsideId: string;
  currPointId: string;
  hoverPointId: string;
  hoverRowNum: string;
  hoverRowsideId: string;
  currRanchViewFeature: RanchViewFeaturesEnum;
  scanDataStatus: ScanDataStatusEnum; // Moved to this slice to make it easier to reset when a new scan is selected
  currBlueberryClasses:
    | FeatureEnum.blueberry
    | FeatureEnum.blueberry_Blue
    | FeatureEnum.blueberry_Green
    | FeatureEnum.blueberry_Purple
    | FeatureEnum.blueberry_Yellow
    | FeatureEnum.flower
    | FeatureEnum.flower_closed
    | FeatureEnum.flower_open
    | FeatureEnum.flower_petalfall
    | null;
  cropTypesOfFilteredBlocks: CropType[];
}

const initialState: SelectState = {
  currCustomerID: '',
  currRanchID: '',
  currBlockID: '',
  currScanID: '',
  currFeature: FeatureEnum.none,
  currAIServiceVersionOverride: '',
  currMeasure: CubeMeasureEnum.detectionFactAvgCountPerMeter,
  currYear: '',
  currRowNum: '',
  currViewDirection: '',
  currRowsideId: '',
  currPointId: '',
  hoverPointId: '',
  hoverRowNum: '',
  hoverRowsideId: '',
  currRanchViewFeature: RanchViewFeaturesEnum.RanchMap,
  scanDataStatus: ScanDataStatusEnum.idle,
  currBlueberryClasses: null,
  cropTypesOfFilteredBlocks: [],
};

export const selectState = createSlice({
  name: 'selectState',
  initialState,
  reducers: {
    initSelectState: () => initialState,
    setCurrCustomerByID: (state, action: PayloadAction<string>) => {
      state.currCustomerID = action.payload;
    },
    setCurrRanchByID: (state, action: PayloadAction<string>) => {
      state.currRanchID = action.payload;
    },
    setCurrBlockByID: (state, action: PayloadAction<string>) => {
      state.currBlockID = action.payload;
    },
    setCurrScanByID: (state, action: PayloadAction<string>) => {
      state.currScanID = action.payload;
      if (action.payload !== '')
        state.scanDataStatus = ScanDataStatusEnum.loading;
    },
    setCurrFeature: (state, action: PayloadAction<FeatureEnum>) => {
      if (isFeatureEnum(action.payload)) {
        state.currFeature = action.payload;
      } else {
        state.currFeature = FeatureEnum.none;
      }
    },
    setCurrAIServiceVersionOverride: (state, action: PayloadAction<string>) => {
      state.currAIServiceVersionOverride = action.payload;
    },
    setCurrMeasure: (state, action: PayloadAction<CubeMeasureEnum>) => {
      state.currMeasure = action.payload;
    },
    setCurrYear: (state, action: PayloadAction<string>) => {
      state.currYear = action.payload;
    },
    setCurrRowNum: (state, action: PayloadAction<string | number>) => {
      if (typeof action.payload === 'number')
        state.currRowNum = action.payload.toString();
      else state.currRowNum = action.payload;
    },
    setCurrViewDirection: (state, action: PayloadAction<string>) => {
      state.currViewDirection = action.payload;
    },
    setCurrRowsideId: (state, action: PayloadAction<string>) => {
      state.currRowsideId = action.payload;
    },
    setCurrPointId: (state, action: PayloadAction<string>) => {
      state.currPointId = action.payload;
    },
    setHoverPointId: (state, action: PayloadAction<string>) => {
      state.hoverPointId = action.payload;
    },
    setHoverRowNum: (state, action: PayloadAction<string | number>) => {
      if (typeof action.payload === 'number')
        state.hoverRowNum = action.payload.toString();
      else state.hoverRowNum = action.payload;
    },
    setHoverRowsideId: (state, action: PayloadAction<string>) => {
      state.hoverRowsideId = action.payload;
    },
    setCurrRanchViewFeature: (
      state,
      action: PayloadAction<RanchViewFeaturesEnum>
    ) => {
      state.currRanchViewFeature = action.payload;
    },
    setScanDataStatus: (state, action: PayloadAction<ScanDataStatusEnum>) => {
      state.scanDataStatus = action.payload;
    },
    setCurrBlueberryClass: (
      state,
      action: PayloadAction<
        | FeatureEnum.blueberry
        | FeatureEnum.blueberry_Blue
        | FeatureEnum.blueberry_Green
        | FeatureEnum.blueberry_Purple
        | FeatureEnum.blueberry_Yellow
        | FeatureEnum.flower
        | FeatureEnum.flower_closed
        | FeatureEnum.flower_open
        | FeatureEnum.flower_petalfall
        | null
      >
    ) => {
      state.currBlueberryClasses = action.payload;
    },
    setCropTypesOfFilteredBlocks: (
      state,
      action: PayloadAction<CropType[]>
    ) => {
      state.cropTypesOfFilteredBlocks = action.payload;
    },

    // these state resets can happen in the above reducers, but this give more flexibility.  For example, a useEffect can set a customer or ranch automatically if there is only 1 and this allows doing that without worrying about accidentally resetting other fields.
    resetSelectFields: (state, action: PayloadAction<ResetItemsEnum[]>) => {
      const resetItems = action.payload;
      if (resetItems.includes(ResetItemsEnum.Customer)) {
        state.currCustomerID = '';
      }

      if (resetItems.includes(ResetItemsEnum.Ranch)) {
        state.currRanchID = '';
      }

      if (resetItems.includes(ResetItemsEnum.Block)) {
        state.currBlockID = '';
      }

      if (resetItems.includes(ResetItemsEnum.Scan)) {
        state.currScanID = '';
      }

      if (resetItems.includes(ResetItemsEnum.Feature)) {
        state.currFeature = FeatureEnum.none;
      }

      if (resetItems.includes(ResetItemsEnum.AIServiceVersionOverride)) {
        state.currAIServiceVersionOverride = '';
      }

      if (resetItems.includes(ResetItemsEnum.Measure)) {
        state.currMeasure = CubeMeasureEnum.detectionFactAvgCountPerMeter;
      }

      if (resetItems.includes(ResetItemsEnum.Year)) {
        state.currYear = '';
      }

      if (resetItems.includes(ResetItemsEnum.RowNum)) {
        state.currRowNum = '';
      }

      if (resetItems.includes(ResetItemsEnum.ViewDirection)) {
        state.currViewDirection = '';
      }

      if (resetItems.includes(ResetItemsEnum.RowsideId)) {
        state.currRowsideId = '';
      }

      if (resetItems.includes(ResetItemsEnum.PointId)) {
        state.currPointId = '';
      }

      if (resetItems.includes(ResetItemsEnum.HoverPointId)) {
        state.hoverPointId = '';
      }

      if (resetItems.includes(ResetItemsEnum.HoverRowNum)) {
        state.hoverRowNum = '';
      }

      if (resetItems.includes(ResetItemsEnum.HoverRowsideId)) {
        state.hoverRowsideId = '';
      }

      if (resetItems.includes(ResetItemsEnum.CurrRanchViewFeature)) {
        state.currRanchViewFeature = RanchViewFeaturesEnum.RanchMap;
      }

      if (resetItems.includes(ResetItemsEnum.CropTypesOfFilteredBlocks)) {
        state.cropTypesOfFilteredBlocks = [];
      }

      // this is a catch all for all cube related fields since they are all reset when a new scan is selected, etc...
      if (resetItems.includes(ResetItemsEnum.AllCubeRelatedFields)) {
        state.currRowNum = '';
        state.currViewDirection = '';
        state.currRowsideId = '';
        state.currPointId = '';
        state.hoverPointId = '';
        state.hoverRowNum = '';
        state.hoverRowsideId = '';
        state.currAIServiceVersionOverride = '';
      }
    },
  },
});

export const {
  initSelectState,
  setCurrCustomerByID,
  setCurrRanchByID,
  setCurrBlockByID,
  setCurrScanByID,
  setCurrFeature,
  setCurrAIServiceVersionOverride,
  setCurrMeasure,
  setCurrYear,
  setCurrRowNum,
  setCurrViewDirection,
  setCurrRowsideId,
  setCurrPointId,
  setHoverPointId,
  setHoverRowNum,
  setHoverRowsideId,
  setCurrRanchViewFeature,
  setScanDataStatus,
  setCurrBlueberryClass,
  setCropTypesOfFilteredBlocks,
  resetSelectFields,
} = selectState.actions;

export const selectCurrCustomerID = (state: RootState) =>
  state.select.currCustomerID;
export const selectCurrRanchID = (state: RootState) => state.select.currRanchID;
export const selectCurrBlockID = (state: RootState) => state.select.currBlockID;
export const selectCurrScanID = (state: RootState) => state.select.currScanID;
export const selectCurrFeature = (state: RootState) => state.select.currFeature;
export const selectCurrAIServiceVersionOverride = (state: RootState) =>
  state.select.currAIServiceVersionOverride;
export const selectCurrMeasure = (state: RootState) => state.select.currMeasure;
export const selectCurrYear = (state: RootState) => state.select.currYear;
export const selectCurrRowNum = (state: RootState) => state.select.currRowNum;
export const selectCurrViewDirection = (state: RootState) =>
  state.select.currViewDirection;
export const selectCurrRowsideId = (state: RootState) =>
  state.select.currRowsideId;
export const selectCurrPointId = (state: RootState) => state.select.currPointId;
export const selectHoverPointId = (state: RootState) =>
  state.select.hoverPointId;
export const selectHoverRowNum = (state: RootState) => state.select.hoverRowNum;
export const selectHoverRowsideId = (state: RootState) =>
  state.select.hoverRowsideId;
export const selectCurrRanchViewFeature = (state: RootState) =>
  state.select.currRanchViewFeature;
export const selectScanDataStatus = (state: RootState) =>
  state.select.scanDataStatus;
export const selectCurrBlueberryClass = (state: RootState) =>
  state.select.currBlueberryClasses;
export const selectCropTypesOfFilteredBlocks = (state: RootState) =>
  state.select.cropTypesOfFilteredBlocks;

export const selectAllSelectState = (state: RootState) => state.select;

export default selectState.reducer;
